/**
 * CropTool.tsx - Image Cropping Tool Component
 *
 * This component provides image cropping functionality with interactive
 * crop area selection, aspect ratio controls, and real-time preview.
 *
 * Key Features:
 * - Interactive crop area selection with drag handles
 * - Aspect ratio presets and custom ratios
 * - Real-time crop preview with zoom controls
 * - Crop area positioning and sizing
 * - Keyboard shortcuts for precise adjustments
 * - Export cropped image with quality settings
 *
 * Cropping Features:
 * - Drag-and-drop crop area positioning
 * - Resize handles for crop area adjustment
 * - Aspect ratio lock and preset options
 * - Zoom in/out for detailed cropping
 * - Grid overlay for alignment assistance
 *
 * Aspect Ratio Presets:
 * - Square (1:1) for profile images and icons
 * - Landscape (16:9) for banners and headers
 * - Portrait (4:5) for social media posts
 * - Custom ratios with manual input
 * - Free-form cropping without constraints
 *
 * Props:
 * - imageUrl: string - URL of image to crop
 * - onCropComplete: (croppedImage: string) => void - Crop completion handler
 * - aspectRatio?: number - Initial aspect ratio constraint
 * - initialCrop?: CropArea - Initial crop area settings
 *
 * Dependencies: Canvas API, image processing utilities, crop calculations
 */

import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import { Button } from "./EditMediaModal";

interface CropToolProps {
  src: string;
  selectedCropPreset?: string;
  onCropPresetChange?: (preset: string) => void;
  onApplyCrop: (cropData: {
    x: number;
    y: number;
    width: number;
    height: number;
    shape: "circle" | "rect";
  }) => void;
  onCancel: () => void;
  onCropModified?: (modified: boolean) => void;
}

export const CropTool: React.FC<CropToolProps> = ({
  src,
  selectedCropPreset = "free",
  onCropPresetChange,
  onApplyCrop,
  onCancel,
  onCropModified,
}) => {
  const [cropArea, setCropArea] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState<string | null>(null);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [imageLoaded, setImageLoaded] = useState(false);
  const [cropModified, setCropModified] = useState(false);
  const imageRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Get aspect ratio from preset
  const getAspectRatioFromPreset = (preset: string): number | null => {
    switch (preset) {
      case "16:9":
        return 16 / 9;
      case "4:3":
        return 4 / 3;
      case "square":
      case "circle":
        return 1;
      default:
        return null;
    }
  };

  const aspectRatio = getAspectRatioFromPreset(selectedCropPreset);
  const isCircle = selectedCropPreset === "circle";

  // Initialize crop area when image loads
  const initializeCropArea = () => {
    if (!imageRef.current || !containerRef.current) return;

    const imageRect = imageRef.current.getBoundingClientRect();
    const containerRect = containerRef.current.getBoundingClientRect();

    // Calculate image position relative to container
    const imageX = imageRect.left - containerRect.left;
    const imageY = imageRect.top - containerRect.top;
    const imageWidth = imageRect.width;
    const imageHeight = imageRect.height;

    // Default to full image dimensions for "Free" crop, 80% for constrained crops
    let cropWidth =
      selectedCropPreset === "free" ? imageWidth : imageWidth * 0.8;
    let cropHeight =
      selectedCropPreset === "free" ? imageHeight : imageHeight * 0.8;

    // Apply aspect ratio constraints
    if (aspectRatio) {
      cropHeight = cropWidth / aspectRatio;
      if (cropHeight > imageHeight * 0.8) {
        cropHeight = imageHeight * 0.8;
        cropWidth = cropHeight * aspectRatio;
      }
    }

    // For circles, ensure minimum size and perfect circle
    if (isCircle) {
      const diameter = Math.max(100, Math.min(cropWidth, cropHeight));
      cropWidth = diameter;
      cropHeight = diameter;
    }

    // Position crop area - full image for "Free", centered for others
    const cropX =
      selectedCropPreset === "free"
        ? imageX
        : imageX + (imageWidth - cropWidth) / 2;
    const cropY =
      selectedCropPreset === "free"
        ? imageY
        : imageY + (imageHeight - cropHeight) / 2;

    setCropArea({
      x: cropX,
      y: cropY,
      width: cropWidth,
      height: cropHeight,
    });
  };

  // Update crop area when preset changes
  useEffect(() => {
    if (imageLoaded) {
      initializeCropArea();
    }
  }, [selectedCropPreset, imageLoaded]);

  // Handle image load
  const handleImageLoad = () => {
    setImageLoaded(true);
    setTimeout(initializeCropArea, 50); // Small delay to ensure DOM is updated
  };

  // Constrain crop area within image bounds
  const constrainCropArea = (newCrop: typeof cropArea) => {
    if (!imageRef.current || !containerRef.current) return newCrop;

    const imageRect = imageRef.current.getBoundingClientRect();
    const containerRect = containerRef.current.getBoundingClientRect();

    const imageX = imageRect.left - containerRect.left;
    const imageY = imageRect.top - containerRect.top;
    const imageWidth = imageRect.width;
    const imageHeight = imageRect.height;

    // Special constraint logic for circles
    if (isCircle) {
      // For resize operations, allow circle to grow to maximum possible size
      if (isResizing) {
        // Maximum diameter is limited by the smaller image dimension
        const maxDiameter = Math.min(imageWidth, imageHeight);
        const constrainedDiameter = Math.max(
          100,
          Math.min(newCrop.width, maxDiameter)
        );

        // For resize, keep the circle size but ensure it stays within bounds
        const maxX = imageX + imageWidth - constrainedDiameter;
        const maxY = imageY + imageHeight - constrainedDiameter;
        const minX = imageX;
        const minY = imageY;

        // Adjust position if circle would go outside image bounds
        let adjustedX = newCrop.x;
        let adjustedY = newCrop.y;

        if (adjustedX < minX) adjustedX = minX;
        if (adjustedX > maxX) adjustedX = maxX;
        if (adjustedY < minY) adjustedY = minY;
        if (adjustedY > maxY) adjustedY = maxY;

        return {
          x: adjustedX,
          y: adjustedY,
          width: constrainedDiameter,
          height: constrainedDiameter,
        };
      } else {
        // For drag operations, never change size - only constrain position
        const maxX = imageX + imageWidth - newCrop.width;
        const maxY = imageY + imageHeight - newCrop.height;
        const minX = imageX;
        const minY = imageY;

        return {
          x: Math.max(minX, Math.min(newCrop.x, maxX)),
          y: Math.max(minY, Math.min(newCrop.y, maxY)),
          width: newCrop.width, // Never change size during drag
          height: newCrop.height,
        };
      }
    }

    // Original constraint logic for non-circles
    let constrainedCrop = { ...newCrop };

    // First, constrain dimensions to image bounds
    constrainedCrop.width = Math.min(constrainedCrop.width, imageWidth);
    constrainedCrop.height = Math.min(constrainedCrop.height, imageHeight);

    // Then constrain position to keep crop within image bounds
    const maxX = imageX + imageWidth - constrainedCrop.width;
    const maxY = imageY + imageHeight - constrainedCrop.height;
    const minX = imageX;
    const minY = imageY;

    constrainedCrop.x = Math.max(minX, Math.min(constrainedCrop.x, maxX));
    constrainedCrop.y = Math.max(minY, Math.min(constrainedCrop.y, maxY));

    return constrainedCrop;
  };

  // Mouse event handlers
  const handleMouseDown = (e: React.MouseEvent, action: "drag" | string) => {
    e.preventDefault();
    e.stopPropagation();

    if (action === "drag") {
      setIsDragging(true);
      setCropModified(true);
      onCropModified?.(true);
      setDragStart({
        x: e.clientX - cropArea.x,
        y: e.clientY - cropArea.y,
      });
    } else {
      setIsResizing(action);
      setCropModified(true);
      onCropModified?.(true);
      setDragStart({ x: e.clientX, y: e.clientY });
    }
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (isDragging) {
      const newX = e.clientX - dragStart.x;
      const newY = e.clientY - dragStart.y;

      const constrainedCrop = constrainCropArea({
        ...cropArea,
        x: newX,
        y: newY,
      });

      setCropArea(constrainedCrop);
    } else if (isResizing) {
      const deltaX = e.clientX - dragStart.x;
      const deltaY = e.clientY - dragStart.y;

      // Only resize if mouse has actually moved significantly (prevent resize on click)
      if (Math.abs(deltaX) < 8 && Math.abs(deltaY) < 8) {
        return;
      }

      let newCrop = { ...cropArea };

      if (isCircle) {
        // Enforce perfect circle constraint
        let newSize = 100; // minimum size

        switch (isResizing) {
          case "se":
            // Bottom-right: use larger of X or Y delta for size change
            const seSizeChange = Math.max(deltaX, deltaY);
            newSize = Math.max(100, cropArea.width + seSizeChange);
            break;
          case "sw":
            // Bottom-left: left and down movement increase size
            const swSizeChange = Math.max(-deltaX, deltaY);
            newSize = Math.max(100, cropArea.width + swSizeChange);
            newCrop.x = cropArea.x + cropArea.width - newSize;
            break;
          case "ne":
            // Top-right: right and up movement increase size
            const neSizeChange = Math.max(deltaX, -deltaY);
            newSize = Math.max(100, cropArea.width + neSizeChange);
            newCrop.y = cropArea.y + cropArea.height - newSize;
            break;
          case "nw":
            // Top-left: left and up movement increase size
            const nwSizeChange = Math.max(-deltaX, -deltaY);
            newSize = Math.max(100, cropArea.width + nwSizeChange);
            newCrop.x = cropArea.x + cropArea.width - newSize;
            newCrop.y = cropArea.y + cropArea.height - newSize;
            break;
        }

        // Always enforce perfect circle
        newCrop.width = newSize;
        newCrop.height = newSize;
      } else {
        // Proper delta-based resizing for rectangles and squares
        switch (isResizing) {
          case "se":
            // Bottom-right corner: grow/shrink from top-left
            newCrop.width = Math.max(100, cropArea.width + deltaX);
            if (aspectRatio) {
              newCrop.height = newCrop.width / aspectRatio;
            } else {
              newCrop.height = Math.max(100, cropArea.height + deltaY);
            }
            break;
          case "sw":
            // Bottom-left corner: grow/shrink from top-right
            const newWidthSW = Math.max(100, cropArea.width - deltaX);
            newCrop.width = newWidthSW;
            newCrop.x = cropArea.x + cropArea.width - newWidthSW;
            if (aspectRatio) {
              newCrop.height = newCrop.width / aspectRatio;
            } else {
              newCrop.height = Math.max(100, cropArea.height + deltaY);
            }
            break;
          case "ne":
            // Top-right corner: grow/shrink from bottom-left
            newCrop.width = Math.max(100, cropArea.width + deltaX);
            if (aspectRatio) {
              newCrop.height = newCrop.width / aspectRatio;
              newCrop.y = cropArea.y + cropArea.height - newCrop.height;
            } else {
              const newHeightNE = Math.max(100, cropArea.height - deltaY);
              newCrop.height = newHeightNE;
              newCrop.y = cropArea.y + cropArea.height - newHeightNE;
            }
            break;
          case "nw":
            // Top-left corner: grow/shrink from bottom-right
            const newWidthNW = Math.max(100, cropArea.width - deltaX);
            newCrop.width = newWidthNW;
            newCrop.x = cropArea.x + cropArea.width - newWidthNW;
            if (aspectRatio) {
              newCrop.height = newCrop.width / aspectRatio;
              newCrop.y = cropArea.y + cropArea.height - newCrop.height;
            } else {
              const newHeightNW = Math.max(100, cropArea.height - deltaY);
              newCrop.height = newHeightNW;
              newCrop.y = cropArea.y + cropArea.height - newHeightNW;
            }
            break;
          case "n":
            if (!aspectRatio) {
              const newHeightN = Math.max(100, cropArea.height - deltaY);
              newCrop.height = newHeightN;
              newCrop.y = cropArea.y + cropArea.height - newHeightN;
            }
            break;
          case "s":
            if (!aspectRatio) {
              newCrop.height = Math.max(100, cropArea.height + deltaY);
            }
            break;
          case "w":
            if (!aspectRatio) {
              const newWidthW = Math.max(100, cropArea.width - deltaX);
              newCrop.width = newWidthW;
              newCrop.x = cropArea.x + cropArea.width - newWidthW;
            }
            break;
          case "e":
            if (!aspectRatio) {
              newCrop.width = Math.max(100, cropArea.width + deltaX);
            }
            break;
        }
      }

      const constrainedCrop = constrainCropArea(newCrop);
      setCropArea(constrainedCrop);

      // Only update drag start if the crop actually changed
      if (
        constrainedCrop.width !== cropArea.width ||
        constrainedCrop.height !== cropArea.height ||
        constrainedCrop.x !== cropArea.x ||
        constrainedCrop.y !== cropArea.y
      ) {
        setDragStart({ x: e.clientX, y: e.clientY });
      }
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setIsResizing(null);
  };

  // Add global mouse event listeners
  useEffect(() => {
    if (isDragging || isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);

      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isDragging, isResizing, cropArea, dragStart]);

  // Get current crop data relative to image coordinates
  const getCropDataRelativeToImage = () => {
    if (!imageRef.current || !containerRef.current) return null;

    const imageRect = imageRef.current.getBoundingClientRect();
    const containerRect = containerRef.current.getBoundingClientRect();

    const imageX = imageRect.left - containerRect.left;
    const imageY = imageRect.top - containerRect.top;
    const imageWidth = imageRect.width;
    const imageHeight = imageRect.height;

    // Calculate crop position relative to image (0-1 range)
    const relativeX = (cropArea.x - imageX) / imageWidth;
    const relativeY = (cropArea.y - imageY) / imageHeight;
    const relativeWidth = cropArea.width / imageWidth;
    const relativeHeight = cropArea.height / imageHeight;

    return {
      x: relativeX,
      y: relativeY,
      width: relativeWidth,
      height: relativeHeight,
    };
  };

  const handleApplyCrop = () => {
  const relativeCropData = getCropDataRelativeToImage();
  if (!relativeCropData) return;

  onApplyCrop({
    ...relativeCropData,
    shape: selectedCropPreset === "circle" ? "circle" : "rect",
  });

  setCropModified(false);
  onCropModified?.(false);
};


  const handleCancel = () => {
    setCropModified(false);
    onCropModified?.(false);
    onCancel();
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        height: "100%",
        background: "#ffffff",
        position: "relative",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          height: "100%",
          background: "#ffffff",
          position: "relative",
        }}
      >
        <div
          ref={containerRef}
          style={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            height: "100%",
            background: "#f8fafc",
            borderRadius: "8px",
            overflow: "hidden",
          }}
        >
          {/* Image */}
          <img
            ref={imageRef}
            src={src}
            alt="Crop preview"
            onLoad={handleImageLoad}
            style={{
              maxWidth: "100%",
              maxHeight: "100%",
              width: "auto",
              height: "auto",
              objectFit: "contain",
              display: "block",
              userSelect: "none",
              borderRadius: 0,
            }}
          />

          {/* Crop overlay and controls - only show after image loads */}
          {imageLoaded && imageRef.current && (
            <>
              {/* Shadow overlays that cover the entire image area */}
              {(() => {
                const imageRect = imageRef.current!.getBoundingClientRect();
                const containerRect =
                  containerRef.current!.getBoundingClientRect();
                const imageX = imageRect.left - containerRect.left;
                const imageY = imageRect.top - containerRect.top;
                const imageWidth = imageRect.width;
                const imageHeight = imageRect.height;

                // For circle crops, use a single overlay with circular cutout
                if (isCircle) {
                  const centerX = cropArea.x + cropArea.width / 2 - imageX;
                  const centerY = cropArea.y + cropArea.height / 2 - imageY;
                  const radius = cropArea.width / 2;

                  return (
                    <div
                      style={{
                        position: "absolute",
                        top: `${imageY}px`,
                        left: `${imageX}px`,
                        width: `${imageWidth}px`,
                        height: `${imageHeight}px`,
                        background: "rgba(0, 0, 0, 0.4)",
                        pointerEvents: "none",
                        zIndex: 1,
                        mask: `radial-gradient(circle at ${centerX}px ${centerY}px, transparent ${radius}px, black ${radius + 1}px)`,
                        WebkitMask: `radial-gradient(circle at ${centerX}px ${centerY}px, transparent ${radius}px, black ${radius + 1}px)`,
                      }}
                    />
                  );
                }

                // For rectangular crops, use the original four overlays
                return (
                  <>
                    {/* Top overlay */}
                    <div
                      style={{
                        position: "absolute",
                        top: `${imageY}px`,
                        left: `${imageX}px`,
                        width: `${imageWidth}px`,
                        height: `${Math.max(0, cropArea.y - imageY)}px`,
                        background: "rgba(0, 0, 0, 0.4)",
                        pointerEvents: "none",
                        zIndex: 1,
                      }}
                    />

                    {/* Bottom overlay */}
                    <div
                      style={{
                        position: "absolute",
                        top: `${cropArea.y + cropArea.height}px`,
                        left: `${imageX}px`,
                        width: `${imageWidth}px`,
                        height: `${Math.max(0, imageY + imageHeight - (cropArea.y + cropArea.height))}px`,
                        background: "rgba(0, 0, 0, 0.4)",
                        pointerEvents: "none",
                        zIndex: 1,
                      }}
                    />

                    {/* Left overlay */}
                    <div
                      style={{
                        position: "absolute",
                        top: `${cropArea.y}px`,
                        left: `${imageX}px`,
                        width: `${Math.max(0, cropArea.x - imageX)}px`,
                        height: `${cropArea.height}px`,
                        background: "rgba(0, 0, 0, 0.4)",
                        pointerEvents: "none",
                        zIndex: 1,
                      }}
                    />

                    {/* Right overlay */}
                    <div
                      style={{
                        position: "absolute",
                        top: `${cropArea.y}px`,
                        left: `${cropArea.x + cropArea.width}px`,
                        width: `${Math.max(0, imageX + imageWidth - (cropArea.x + cropArea.width))}px`,
                        height: `${cropArea.height}px`,
                        background: "rgba(0, 0, 0, 0.4)",
                        pointerEvents: "none",
                        zIndex: 1,
                      }}
                    />
                  </>
                );
              })()}

              {/* Crop selection frame */}
              <div
                style={{
                  position: "absolute",
                  left: `${cropArea.x}px`,
                  top: `${cropArea.y}px`,
                  width: `${cropArea.width}px`,
                  height: `${cropArea.height}px`,
                  border: "3px solid #3b82f6",
                  borderRadius: isCircle ? "50%" : "0",
                  cursor: "move",
                  pointerEvents: "auto",
                  zIndex: 2,
                  boxShadow: "0 2px 8px rgba(59, 130, 246, 0.3)",
                  background: "transparent",
                }}
                onMouseDown={(e) => handleMouseDown(e, "drag")}
              >
                {/* Corner resize handles */}
                <div
                  style={{
                    position: "absolute",
                    width: "12px",
                    height: "12px",
                    background: "#3b82f6",
                    border: "2px solid #ffffff",
                    borderRadius: "50%",
                    cursor: "nw-resize",
                    top: "-8px",
                    left: "-8px",
                    opacity: isCircle ? 0 : 1,
                    pointerEvents: isCircle ? "none" : "auto",
                    visibility: isCircle ? "hidden" : "visible",
                    zIndex: 3,
                  }}
                  onMouseDown={(e) => handleMouseDown(e, "nw")}
                />
                <div
                  style={{
                    position: "absolute",
                    width: "12px",
                    height: "12px",
                    background: "#3b82f6",
                    border: "2px solid #ffffff",
                    borderRadius: "50%",
                    cursor: "ne-resize",
                    top: "-8px",
                    right: "-8px",
                    opacity: isCircle ? 0 : 1,
                    pointerEvents: isCircle ? "none" : "auto",
                    visibility: isCircle ? "hidden" : "visible",
                    zIndex: 3,
                  }}
                  onMouseDown={(e) => handleMouseDown(e, "ne")}
                />
                <div
                  style={{
                    position: "absolute",
                    width: "12px",
                    height: "12px",
                    background: "#3b82f6",
                    border: "2px solid #ffffff",
                    borderRadius: "50%",
                    cursor: "sw-resize",
                    bottom: "-8px",
                    left: "-8px",
                    opacity: isCircle ? 0 : 1,
                    pointerEvents: isCircle ? "none" : "auto",
                    visibility: isCircle ? "hidden" : "visible",
                    zIndex: 3,
                  }}
                  onMouseDown={(e) => handleMouseDown(e, "sw")}
                />
                <div
                  style={{
                    position: "absolute",
                    width: "12px",
                    height: "12px",
                    background: "#3b82f6",
                    border: "2px solid #ffffff",
                    borderRadius: "50%",
                    cursor: "se-resize",
                    bottom: "-8px",
                    right: "-8px",
                    zIndex: 3,
                  }}
                  onMouseDown={(e) => handleMouseDown(e, "se")}
                />

                {/* Edge resize handles (only for free crop) */}
                {!aspectRatio && (
                  <>
                    <div
                      style={{
                        position: "absolute",
                        width: "24px",
                        height: "6px",
                        background: "#3b82f6",
                        border: "1px solid #ffffff",
                        borderRadius: "3px",
                        cursor: "n-resize",
                        top: "-4px",
                        left: "50%",
                        transform: "translateX(-50%)",
                        zIndex: 3,
                      }}
                      onMouseDown={(e) => handleMouseDown(e, "n")}
                    />
                    <div
                      style={{
                        position: "absolute",
                        width: "24px",
                        height: "6px",
                        background: "#3b82f6",
                        border: "1px solid #ffffff",
                        borderRadius: "3px",
                        cursor: "s-resize",
                        bottom: "-4px",
                        left: "50%",
                        transform: "translateX(-50%)",
                        zIndex: 3,
                      }}
                      onMouseDown={(e) => handleMouseDown(e, "s")}
                    />
                    <div
                      style={{
                        position: "absolute",
                        width: "6px",
                        height: "24px",
                        background: "#3b82f6",
                        border: "1px solid #ffffff",
                        borderRadius: "3px",
                        cursor: "w-resize",
                        top: "50%",
                        left: "-4px",
                        transform: "translateY(-50%)",
                        zIndex: 3,
                      }}
                      onMouseDown={(e) => handleMouseDown(e, "w")}
                    />
                    <div
                      style={{
                        position: "absolute",
                        width: "6px",
                        height: "24px",
                        background: "#3b82f6",
                        border: "1px solid #ffffff",
                        borderRadius: "3px",
                        cursor: "e-resize",
                        top: "50%",
                        right: "-4px",
                        transform: "translateY(-50%)",
                        zIndex: 3,
                      }}
                      onMouseDown={(e) => handleMouseDown(e, "e")}
                    />
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>
      <Button
        variant="primary"
        onClick={() => handleApplyCrop()}
        style={{ marginTop: 5, padding: "5px 20px", marginBottom: 15 }}
      >
        Apply
      </Button>
    </div>
  );
};
